import NestedPathObject = ObjectTypes.NestedPathObject;
import ObjectDefaultPropType = ObjectTypes.ObjectDefaultPropType;

function deepCopy<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

Object.assignNested = function <T>(target: T, source: NestedPathObject<T>): T {
	const targetClone = deepCopy(target);
	const keyPaths: string[] = Object.keys(source);
	keyPaths.forEach(keyPath => {
		const value = source[keyPath];
		const keys = keyPath.split('.');
		let currentTarget = targetClone;
		keys.forEach((key, index) => {
			if (index === keys.length - 1) {
				currentTarget[key] = value;
			} else {
				currentTarget = currentTarget[key];
			}
		});
	});
	return targetClone;
};

Object.initDefaultObject = function <T extends object>(
	object: T,
	force = false,
	defaultsOverrides: Partial<Record<ObjectDefaultPropType, any>> = {
		string: '',
		number: NaN,
		boolean: false,
		object: {},
		array: [],
		null: null,
	},
): T {
	const objectClone = deepCopy(object);
	const keys: string[] = Object.keys(objectClone);
	keys.forEach(key => {
		const value = objectClone[key];
		if (value === undefined || force) {
			objectClone[key] = defaultsOverrides[typeof value];
		}
	});
	return objectClone;
};

Object.flattenValues = function <T extends object>(object: T): T[keyof T][] {
	const values: T[keyof T][] = [];
	Object.keys(object).forEach(key => {
		const value = object[key];
		if (typeof value === 'object') {
			values.push(...Object.flattenValues(value));
		} else {
			values.push(value);
		}
	});
	return values;
};

Object.flattenKeys = function <T, K extends keyof T>(object: T): K[] {
	return Object.keys(object) as K[];
};

Object.hasValue = function hasValue<T>(enumType: T, value: any): value is T[keyof T] {
	if (!value || !enumType) return false;
	for (const key in enumType) {
		if (enumType[key] === value) return true;
	}
	return false;
};

Object.reassign = function <T>(target: T, source: T): T {
	const targetClone = deepCopy(target);
	const keys: string[] = Object.keys(targetClone);
	keys.forEach(key => {
		if (isNullish(source[key])) {
			delete targetClone[key];
		} else if (typeof source[key] === 'object') {
			targetClone[key] = Object.reassign(targetClone[key], source[key]);
		} else {
			targetClone[key] = source[key];
		}
	});
	return targetClone;
};

globalThis.clearNullish = function <T extends object>(obj: T): NonNullable<T> {
	Object.keys(obj).forEach(key => {
		if (obj[key] == null || obj[key] === undefined) delete obj[key];
	});
	return obj;
};

/* -------------------------------------------------------------------------------------------------------------- */
/* ------------  Deep Object Definition ------------ */
Object.getDeepValue = function getDeepValue<T, P extends string>(obj: T, path: P): DeepObjectValue<T, P> {
	// Split the path into keys
	const keys = path.split('.') as string[];

	// Iterate over the keys and reduce the object to the nested value
	return keys.reduce((acc: any, key: string) => {
		return acc && acc[key] !== undefined ? acc[key] : undefined;
	}, obj);
};

Object.setDeepValue = function setDeepValue<T, P extends string, V>(obj: T, path: P, value: V, ignoreUnkownKeys = true): T {
	// Split the path into keys
	const keys = path.split('.') as string[];

	// Iterate over the keys and reduce the object to the nested value
	keys.reduce((acc: any, key: string, index: number) => {
		if (ignoreUnkownKeys && acc[key] === undefined) return obj;
		if (index === keys.length - 1) {
			acc[key] = value;
		} else {
			if (acc[key] === undefined) {
				acc[key] = {};
			}
			return acc[key];
		}
	}, obj);

	return obj;
};

Object.deleteDeepValue = function deleteDeepValue<T, P extends string>(obj: T, path: P): T {
	// Split the path into keys
	const keys = path.split('.') as string[];

	// Iterate over the keys and reduce the object to the nested value
	keys.reduce((acc: any, key: string, index: number) => {
		if (index === keys.length - 1) {
			delete acc[key];
		} else {
			return acc[key];
		}
	}, obj);

	return obj;
};

Object.getValueFromSymbol = function <T>(obj: unknown, symbol: symbol): T {
	if (typeof obj === 'object' && obj && symbol in obj) {
		return (obj as {[key: symbol]: any})[symbol];
	}
	return undefined;
};

Object.deepFreeze = function <T>(obj: T): import('@angular-devkit/architect/src/jobs/types').DeepReadonly<T> {
	if (obj) {
		Object.freeze(obj);

		const oIsFunction = typeof obj === 'function';
		const hasOwnProp = Object.prototype.hasOwnProperty;

		Object.getOwnPropertyNames(obj).forEach(function (prop: string) {
			if (hasOwnProp.call(obj, prop) && (oIsFunction ? prop !== 'caller' && prop !== 'callee' && prop !== 'arguments' : true)) {
				const propValue = obj[prop as keyof T];
				if (propValue !== null && (typeof propValue === 'object' || typeof propValue === 'function') && !Object.isFrozen(propValue)) {
					Object.deepFreeze(propValue);
				}
			}
		});
	}

	return obj as import('@angular-devkit/architect/src/jobs/types').DeepReadonly<T>;
};

/* -------------------------------------------------------------------------------------------------------------- */
function jsonize<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

export function isKeyOf<T extends StringRecord<string>, K extends keyof T>(obj: T, key: K): key is K {
	return key in obj;
}

export function isValueOf<T extends StringRecord<string>>(obj: T, value: unknown): value is T[keyof T] {
	return Object.values(obj).includes(value as string);
}

export {};
